import React, { useState, useEffect, useCallback } from 'react';
import Chart from '../components/Chart';
import DataImportSection from '../components/DataImportSection';

const BTCoinBacktestMock: React.FC = () => {
  
  // 백테스트 설정
  const [backtestSpeed, setBacktestSpeed] = useState<number>(1);
  const [backtestStatus, setBacktestStatus] = useState<string>('대기중');
  const [earliestKeyData] = useState<{
    earliestKey: string;
    groupedData: any;
    changeRateAnalysis: any;
  } | null>(null);
  
  // 시간 범위 설정
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('all');
  const [importHistory, setImportHistory] = useState<Array<{
    id: number;
    startTime: string;
    endTime: string;
    label: string;
    importTime: string;
  }>>([]);
  
  // 코인 선택
  const [selectedCoin, setSelectedCoin] = useState<string>('');
  const [availableCoins, setAvailableCoins] = useState<string[]>([]);
  const [coinsLoading, setCoinsLoading] = useState(false);
  
  // Mock용 코인 목록 조회
  const refetchCoins = async () => {
    setCoinsLoading(true);
    try {
      const response = await fetch('/mercury-backend/bt/coin-list?key_prefix=bt:upbit:ticker&date_filter=2025-09-01');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setAvailableCoins(result.data);
      } else {
        setAvailableCoins([]);
      }
    } catch (err) {
      console.error('Error fetching coin list:', err);
      setAvailableCoins([]);
    } finally {
      setCoinsLoading(false);
    }
  };
  
  useEffect(() => {
    refetchCoins();
  }, []);

  // 백테스트 데이터 조회 (선택한 코인만) - Mock 버전용 커스텀 hook
  const [mockData, setMockData] = useState<any[] | null>(null);
  const [mockLoading, setMockLoading] = useState(false);
  const [mockError, setMockError] = useState<string | null>(null);
  
  const mockRefetch = useCallback(async (coinCode?: string) => {
    setMockLoading(true);
    setMockError(null);
    try {
      let url = `/mercury-backend/bt/coin-backtest?key_prefix=bt:upbit:ticker&date_filter=2025-09-01`;
      if (coinCode) {
        url += `&coin_code=${encodeURIComponent(coinCode)}`;
      }
      // 선택한 시간 범위가 있으면 백엔드에 전달
      if (selectedTimeRange !== 'all') {
        const importItem = importHistory.find(item => item.id.toString() === selectedTimeRange);
        if (importItem) {
          // 시간대 정보가 없으면 'Z'를 붙여서 UTC로 전달
          let startTime = importItem.startTime;
          let endTime = importItem.endTime;
          // T가 있지만 Z나 +, - (타임존)이 없으면 Z를 붙임
          if (startTime.includes('T') && !startTime.includes('Z') && !startTime.match(/[+-]\d{2}:\d{2}$/)) {
            startTime = startTime + 'Z';
          } else if (!startTime.includes('T') && !startTime.includes('Z') && !startTime.match(/[+-]\d{2}:\d{2}$/)) {
            startTime = startTime + 'Z';
          }
          if (endTime.includes('T') && !endTime.includes('Z') && !endTime.match(/[+-]\d{2}:\d{2}$/)) {
            endTime = endTime + 'Z';
          } else if (!endTime.includes('T') && !endTime.includes('Z') && !endTime.match(/[+-]\d{2}:\d{2}$/)) {
            endTime = endTime + 'Z';
          }
          url += `&start_time=${encodeURIComponent(startTime)}&end_time=${encodeURIComponent(endTime)}`;
          console.log('🔍 [코인 백테스트 Mock] 시간 범위 필터링:', { startTime, endTime, url });
        }
      }
      const response = await fetch(url);
      const result = await response.json();
      if (!response.ok) {
        const errorMessage = result.error || result.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }
      const dataArray = result.data || [];
      console.log('📊 [코인 백테스트 Mock] 데이터 조회 완료:', {
        success: result.success,
        dataLength: dataArray.length,
        count: result.count,
        selectedTimeRange,
        sampleData: dataArray.slice(0, 2)
      });
      setMockData(dataArray);
      if (result.extra && result.extra.parseErrors && result.extra.parseErrors.length > 0) {
        const errorCount = result.extra.errorCount || result.extra.parseErrors.length;
        const errorMessages = result.extra.parseErrors.slice(0, 10);
        const errorMessage = `키 파싱 에러 ${errorCount}개 발생:\n${errorMessages.join('\n')}${errorCount > 10 ? `\n... 외 ${errorCount - 10}개 에러` : ''}`;
        setMockError(errorMessage);
      } else {
        setMockError(null);
      }
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setMockError(errorMessage);
      console.error('Error fetching coin backtest data:', err);
      return null;
    } finally {
      setMockLoading(false);
    }
  }, [selectedTimeRange, importHistory]);
  
  // 코인 선택 또는 시간 범위 선택 변경 시 데이터 다시 조회
  useEffect(() => {
    // importHistory가 로드되거나 전체 기간 선택 시에만 실행
    if (importHistory.length > 0 || selectedTimeRange === 'all') {
      mockRefetch(selectedCoin || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCoin, selectedTimeRange]);

  // 코인 백테스트 Mock 실행 함수 (Mock 섹션용)
  const handleCoinBacktestMockStart = useCallback(async () => {
    console.log(`코인 백테스트 Mock 시작 - 속도: 병렬 ${backtestSpeed}개`);
    setBacktestStatus('실행중...');
    
    try {
      // 요청 body 구성
      const requestBody: any = {
        key_prefix: 'bt:upbit:ticker'
      };
      
      // 코인 코드가 있으면 추가
      if (selectedCoin) {
        requestBody.coin_code = selectedCoin;
      }
      
      // 선택한 시간 범위가 있으면 추가
      if (selectedTimeRange !== 'all') {
        const importItem = importHistory.find(item => item.id.toString() === selectedTimeRange);
        if (importItem) {
          // 시간대 정보가 없으면 그대로 사용 (백엔드에서 처리)
          let startTime = importItem.startTime;
          let endTime = importItem.endTime;
          // T가 있지만 Z나 +, - (타임존)이 없으면 그대로 전달 (백엔드에서 처리)
          requestBody.start_time = startTime;
          requestBody.end_time = endTime;
        }
      }
      
      console.log('📤 Mock 테스트 요청:', requestBody);
      
      const response = await fetch('http://localhost:8082/mock-test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ 코인 백테스트 Mock 완료:', data);
      
      // 백테스트 상태 업데이트
      if (data.status === 'completed') {
        setBacktestStatus(`완료 (${data.processedData || 0}개 키 처리)`);
      } else {
        setBacktestStatus('완료');
      }
      
    } catch (error) {
      console.error('❌ 코인 백테스트 Mock 실행 오류:', error);
      setBacktestStatus('오류');
    }
  }, [backtestSpeed, selectedCoin, selectedTimeRange, importHistory]);

  // Redis 데이터를 차트 데이터로 변환하는 함수
  const convertRedisDataToChartData = (redisData: any[]) => {
    if (!redisData || redisData.length === 0) {
      console.log('⚠️ [코인 백테스트 Mock] 변환할 데이터가 없습니다');
      return [];
    }
    
    console.log('🔄 [코인 백테스트 Mock] 데이터 변환 시작:', {
      원본데이터개수: redisData.length,
      샘플원본데이터: redisData.slice(0, 2)
    });
    
    const converted = redisData.map(item => {
      // 백엔드에서 반환하는 형식 (대문자 시작) 또는 프론트엔드 형식 (소문자) 모두 지원
      const stockCode = item.stock_code || item.StockCode || '';
      const stockName = item.stock_name || item.StockName || stockCode;
      const price = item.price || item.Price || 0;
      const changeRate = item.change_rate || item.ChangeRate || 0;
      const volume = item.volume || item.Volume || 0;
      
      // timestamp 처리: 백엔드에서 밀리초 단위 int64로 반환하므로 number로 유지
      let timestamp: number;
      const ts = item.timestamp || item.Timestamp;
      if (ts !== undefined && ts !== null) {
        if (typeof ts === 'number') {
          // 백엔드에서 밀리초 단위로 반환하므로 그대로 사용
          timestamp = ts;
        } else if (typeof ts === 'string') {
          // ISO string인 경우 밀리초로 변환
          timestamp = new Date(ts).getTime();
        } else {
          timestamp = Date.now();
        }
      } else {
        timestamp = Date.now();
      }
      
      return {
        stock_code: stockCode,
        stock_name: stockName,
        price: price,
        change_rate: changeRate,
        volume: volume,
        timestamp: timestamp, // number로 유지 (밀리초)
        created_at: timestamp // number로 유지 (밀리초)
      };
    });
    
    console.log('✅ [코인 백테스트 Mock] 데이터 변환 완료:', {
      변환된데이터개수: converted.length,
      샘플변환데이터: converted.slice(0, 2)
    });
    
    return converted;
  };

  // 백테스트 그룹화된 데이터를 차트 데이터로 변환하는 함수
  const convertGroupedDataToChartData = (groupedData: any) => {
    if (!groupedData) return [];
    
    const chartData: any[] = [];
    
    // 각 시간대별 데이터를 순회하여 모든 데이터를 포함
    Object.keys(groupedData).forEach(timestampKey => {
      const timeGroup = groupedData[timestampKey];
      if (Array.isArray(timeGroup)) {
        timeGroup.forEach((item: any) => {
          // 모든 시간대의 데이터를 포함 (백테스트용)
          chartData.push({
            stock_code: item.stock_code,
            stock_name: item.stock_name,
            price: item.price,
            change_rate: item.change_rate,
            volume: item.volume,
            created_at: item.created_at || item.timestamp || item.collected_at
          });
        });
      }
    });
    
    console.log('🔄 [코인 백테스트 Mock] 데이터 변환:', {
      그룹개수: Object.keys(groupedData).length,
      변환된데이터개수: chartData.length,
      샘플데이터: chartData.slice(0, 3)
    });
    
    return chartData;
  };

  // 임포트 이력 조회
  useEffect(() => {
    const fetchImportHistory = async () => {
      try {
        const response = await fetch(
          `/mercury-backend/bt/import-history?tableName=${encodeURIComponent('upbit_ticker_mock')}`
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const histories = result.data || [];
        
        // 이력을 드롭다운 형식으로 변환
        const formattedHistories = histories.map((item: any) => ({
          id: item.id,
          startTime: item.start_time,
          endTime: item.end_time,
          label: `${item.start_time} ~ ${item.end_time}`,
          importTime: item.import_time
        }));

        setImportHistory(formattedHistories);
      } catch (err) {
        console.error('임포트 이력 조회 오류:', err);
        setImportHistory([]);
      }
    };

    fetchImportHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 초기 로드 시 한 번만 실행

  // 데이터 임포트 후 임포트 이력 다시 조회
  const handleDataImported = () => {
    refetchCoins();
    mockRefetch(selectedCoin || undefined);
    // 약간의 지연을 두어 MySQL에 데이터가 저장될 시간 확보
    setTimeout(() => {
      const fetchImportHistory = async () => {
        try {
          const response = await fetch(
            `/mercury-backend/bt/import-history?tableName=${encodeURIComponent('upbit_ticker_mock')}`
          );
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          const histories = result.data || [];
          
          // 이력을 드롭다운 형식으로 변환
          const formattedHistories = histories.map((item: any) => ({
            id: item.id,
            startTime: item.start_time,
            endTime: item.end_time,
            label: `${item.start_time} ~ ${item.end_time}`,
            importTime: item.import_time
          }));

          setImportHistory(formattedHistories);
        } catch (err) {
          console.error('임포트 이력 조회 오류:', err);
        }
      };
      fetchImportHistory();
    }, 1000);
  };

  // 백엔드에서 이미 필터링된 데이터를 사용하므로 추가 필터링 불필요
  const filteredMockData = mockData || [];

  return (
    <div>
      <DataImportSection
        title="1. Mock 데이터를 로컬 MySQL의 upbit_ticker_mock 테이블에서 임포트"
        tableName="upbit_ticker_mock"
        onDataImported={handleDataImported}
      />

      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>2. 코인 백테스트 (Mock)</h4>
        
        {/* 임포트 이력 드롭다운 */}
        {importHistory.length > 0 && (
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#495057' }}>
              임포트 이력 선택:
            </label>
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
            >
              <option value="all">전체 기간</option>
              {importHistory.map((item) => (
                <option key={item.id} value={item.id.toString()}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        )}
        
        {/* 코인 선택 드롭다운 */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#495057' }}>
            코인 선택:
          </label>
          <select
            value={selectedCoin}
            onChange={(e) => setSelectedCoin(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '14px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              backgroundColor: '#fff',
              cursor: 'pointer'
            }}
            disabled={coinsLoading}
          >
            <option value="">전체 코인</option>
            {availableCoins.map((coin) => (
              <option key={coin} value={coin}>
                {coin}
              </option>
            ))}
          </select>
          {coinsLoading && (
            <div style={{ marginTop: '5px', fontSize: '12px', color: '#6c757d' }}>
              코인 목록 로딩 중...
            </div>
          )}
        </div>
        
        {/* 키 파싱 에러 표시 */}
        {mockError && (
          <div style={{ 
            marginBottom: '15px',
            padding: '12px 16px', 
            backgroundColor: '#fee', 
            border: '1px solid #fcc', 
            borderRadius: '6px', 
            fontSize: '13px', 
            color: '#c33',
            whiteSpace: 'pre-wrap',
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>⚠️ 키 파싱 에러</div>
            <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{mockError}</div>
          </div>
        )}
        
        <div style={{ 
          padding: '12px 16px', 
          backgroundColor: '#e3f2fd', 
          border: '1px solid #bbdefb', 
          borderRadius: '6px', 
          fontSize: '14px', 
          color: '#0d47a1', 
          width: '100%',
          height: '130px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <div style={{ marginBottom: '6px', fontWeight: '500' }}>
            백테스트 설정 및 실행
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            병렬 
            <input
              type="number"
              min="1"
              max="100"
              step="1"
              value={backtestSpeed}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 1;
                setBacktestSpeed(Math.max(1, Math.min(100, value)));
              }}
              style={{
                padding: '2px 6px',
                border: '1px solid #ced4da',
                borderRadius: '3px',
                fontSize: '12px',
                width: '50px',
                textAlign: 'center',
                backgroundColor: '#ffffff'
              }}
            />
            <span>개</span>
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px' }}>
            데이터 소스: <strong>Redis (bt:upbit:ticker:*:*, 2025-09-01만)</strong>
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px' }}>
            백테스트 상태: <strong>{backtestStatus}</strong>
          </div>
        </div>
        
        <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
          <button 
            onClick={handleCoinBacktestMockStart}
            style={{ 
              flex: 1, 
              width: '100%', 
              padding: '10px 16px', 
              backgroundColor: '#17a2b8', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              fontSize: '14px', 
              fontWeight: '500' 
            }}
          >
            🚀 코인 백테스트 Mock 시작
          </button>
        </div>
      </div>

      <div style={{ flex: 1 }}>
        {mockLoading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            fontSize: '18px',
            color: '#6c757d'
          }}>
            로딩 중...
          </div>
        ) : mockError ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '8px',
            fontSize: '18px',
            color: '#c33'
          }}>
            오류: {mockError}
          </div>
        ) : filteredMockData && filteredMockData.length > 0 ? (
          <Chart
            data={earliestKeyData ? convertGroupedDataToChartData(earliestKeyData.groupedData) : convertRedisDataToChartData(filteredMockData)}
            loading={mockLoading}
            error={mockError}
            onRefetch={() => mockRefetch(selectedCoin || undefined)}
            isBacktest={true}
            usePrice={true}
            height={800}
          />
        ) : (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            fontSize: '18px',
            color: '#6c757d'
          }}>
            데이터가 없습니다 (2025-09-01의 Redis 키가 없거나 필터링 결과가 없습니다)
          </div>
        )}
      </div>

      {/* timestamp별로 그룹화된 데이터 표시 */}
      {earliestKeyData && earliestKeyData.groupedData && (
        <div style={{ 
          marginTop: '20px', 
          padding: '15px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px', 
          border: '1px solid #e9ecef' 
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#495057' }}>📊 가장 빠른 키의 초별 그룹화된 데이터</h4>
          <div style={{ 
            padding: '12px 16px', 
            backgroundColor: '#e8f5e8', 
            border: '1px solid #c3e6c3', 
            borderRadius: '6px', 
            fontSize: '14px', 
            color: '#155724'
          }}>
            <div style={{ marginBottom: '8px' }}>
              <strong>가장 빠른 키:</strong> {earliestKeyData.earliestKey}
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>초별 데이터:</strong>
            </div>
            {Object.entries(earliestKeyData.groupedData).map(([timestamp, data]) => (
              <div key={timestamp} style={{ 
                marginBottom: '15px',
                backgroundColor: '#ffffff', 
                border: '1px solid #d4edda', 
                borderRadius: '4px', 
                padding: '10px'
              }}>
                <div style={{ 
                  fontWeight: 'bold', 
                  marginBottom: '8px', 
                  color: '#0d47a1',
                  fontSize: '13px'
                }}>
                  📅 {timestamp} ({Array.isArray(data) ? data.length : 1}개 항목)
                </div>
                <div style={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto',
                  fontSize: '11px',
                  fontFamily: 'monospace',
                  backgroundColor: '#f8f9fa',
                  padding: '8px',
                  borderRadius: '3px'
                }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(data, null, 2)}
                  </pre>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BTCoinBacktestMock;

